import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ClientApiAdminClient, ClientApiAdminService, ClientApiDocsLinks } from '../../../services/client-api-admin.service';
import { ClientApiDeveloperLinks, ClientApiPortalAccess, ClientApiPortalService } from '../../../services/client-api-portal.service';

@Component({
  selector: 'app-client-api-access-widget',
  templateUrl: './client-api-access-widget.component.html',
  styleUrls: ['./client-api-access-widget.component.scss']
})
export class ClientApiAccessWidgetComponent implements OnInit, OnChanges {
  @Input() brandId: number;
  @Input() mode: 'client' | 'admin' = 'client';

  clients: ClientApiAdminClient[] = [];
  portal: ClientApiPortalAccess | null = null;
  clientApiBaseUrl = '';
  selectedClient: ClientApiAdminClient | null = null;
  activityData: any = null;
  loading = false;
  message = '';
  error = '';
  generatedSecret = '';
  generatedClientUuid = '';
  generatedClientName = '';
  generatedEnvironment = '';
  generatedAt = '';

  constructor(
    private portalService: ClientApiPortalService,
    private adminService: ClientApiAdminService
  ) {}

  ngOnInit(): void {
    this.loadAccess();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.brandId && !changes.brandId.firstChange) {
      this.loadAccess();
    }
  }

  get canManageSecrets(): boolean {
    return this.mode === 'admin' || !!(this.portal && this.portal.can_manage_secrets);
  }

  get clientApiPortalUrl(): string {
    const portalBrandId = Number(this.brandId || (this.portal && this.portal.brand_id) || 0);
    return this.adminService.portalUrlForBrand(portalBrandId, this.clientApiBaseUrl);
  }

  get clientApiSampleAppUrl(): string {
    return this.adminService.sampleAppUrlFor(this.clientApiBaseUrl);
  }

  get clientApiSampleSourceUrl(): string {
    return this.adminService.sampleSourceUrl;
  }

  canLaunchPlayground(client: ClientApiAdminClient): boolean {
    return client.status === 'active'
      && !client.access_blocked;
  }

  canLaunchLiveTest(client: ClientApiAdminClient): boolean {
    return this.mode === 'admin'
      && client.status === 'active'
      && !client.access_blocked
      && client.environment_mode === 'live'
      && client.sync_enabled
      && client.live_test_enabled
      && client.scopes.indexOf('jobs:create') >= 0;
  }

  private openHostedLaunchWindow(): Window | null {
    const launchWindow = window.open('', '_blank');
    if (!launchWindow || launchWindow.closed) {
      return null;
    }

    launchWindow.document.title = 'Opening My Investigations Corp';
    launchWindow.document.body.style.fontFamily = 'Arial, sans-serif';
    launchWindow.document.body.style.padding = '32px';
    launchWindow.document.body.textContent = 'Opening your secure hosted session...';
    return launchWindow;
  }

  launchPlayground(client: ClientApiAdminClient): void {
    if (!this.canLaunchPlayground(client)) {
      this.error = 'The hosted playground requires an active, unblocked API client. The launched session is always isolated sandbox mode.';
      return;
    }

    const launchWindow = this.openHostedLaunchWindow();
    if (!launchWindow) {
      this.error = 'A new window is required so this page stays open. Allow pop-ups for Sharmans.Online, then try again.';
      return;
    }

    this.beginRequest();
    const request = this.mode === 'admin'
      ? this.adminService.playgroundLaunch(client.client_uuid, Number(client.brand_id))
      : this.portalService.playgroundLaunch(client.client_uuid);

    request.subscribe({
      next: response => {
        const launchUrl = response && response.data ? response.data.launch_url : '';
        this.loading = false;
        if (!launchUrl) {
          launchWindow.close();
          this.error = 'The hosted playground launch URL was not returned.';
          return;
        }
        launchWindow.location.href = launchUrl;
        launchWindow.opener = null;
        launchWindow.focus();
      },
      error: error => {
        launchWindow.close();
        this.handleError(error, 'The hosted sandbox playground could not be opened.');
      }
    });
  }

  launchLiveTest(client: ClientApiAdminClient): void {
    if (!this.canLaunchLiveTest(client)) {
      this.error = 'Live Test must be enabled by an administrator and requires an active live credential with Sync writes and jobs:create.';
      return;
    }

    const launchWindow = this.openHostedLaunchWindow();
    if (!launchWindow) {
      this.error = 'A new window is required so this page stays open. Allow pop-ups for Sharmans.Online, then try again.';
      return;
    }
    window.focus();

    const confirmation = window.prompt(
      'LIVE TEST creates real SO jobs, notes and files and can trigger normal automation and notifications. Type LIVE to continue.'
    );
    if (confirmation !== 'LIVE') {
      launchWindow.close();
      return;
    }

    this.beginRequest();
    this.adminService.liveTestLaunch(client.client_uuid, Number(client.brand_id)).subscribe({
      next: response => {
        const launchUrl = response && response.data ? response.data.launch_url : '';
        this.loading = false;
        if (!launchUrl) {
          launchWindow.close();
          this.error = 'The hosted Live Test launch URL was not returned.';
          return;
        }
        launchWindow.location.href = launchUrl;
        launchWindow.opener = null;
        launchWindow.focus();
      },
      error: error => {
        launchWindow.close();
        this.handleError(error, 'The hosted Live Test session could not be opened.');
      }
    });
  }

  loadAccess(): void {
    if (this.mode === 'admin' && (!this.brandId || Number(this.brandId) <= 0)) {
      return;
    }

    this.beginRequest();
    const request = this.mode === 'admin'
      ? this.adminService.listClients(this.adminService.selectedBaseUrl, 200, Number(this.brandId))
      : this.portalService.access();

    request.subscribe({
      next: response => {
        const data = response && response.data ? response.data : {};
        this.clients = data.clients || [];
        this.portal = this.mode === 'client' ? (data.portal || null) : null;
        this.clientApiBaseUrl = data.client_api_base_url || this.adminService.selectedBaseUrl;
        this.loading = false;
      },
      error: error => this.handleError(error, 'Client API access could not be loaded.')
    });
  }

  openDeveloperResource(client: ClientApiAdminClient, kind: 'guide' | 'docs' | 'openapi'): void {
    this.beginRequest();
    const request: any = this.mode === 'admin'
      ? this.adminService.docsLinks(client.client_uuid)
      : this.portalService.developerLinks(client.client_uuid);

    request.subscribe({
      next: response => {
        const links: ClientApiDocsLinks | ClientApiDeveloperLinks = response && response.data ? response.data : null;
        this.loading = false;
        if (!links) {
          this.error = 'Developer documentation links were not returned.';
          return;
        }

        let url = '';
        if (kind === 'guide') {
          url = (links as any).client_guide_url || (links as any).guide_url;
        } else if (kind === 'docs') {
          url = (links as any).client_docs_url || (links as any).docs_url;
        } else {
          url = (links as any).client_openapi_url || (links as any).openapi_url;
        }

        if (!url) {
          this.error = 'The requested developer link is not configured.';
          return;
        }
        window.open(url, '_blank', 'noopener');
      },
      error: error => this.handleError(error, 'Developer documentation could not be opened.')
    });
  }

  loadActivity(client: ClientApiAdminClient): void {
    this.beginRequest();
    this.selectedClient = client;
    this.activityData = null;
    const request = this.mode === 'admin'
      ? this.adminService.activity(this.adminService.selectedBaseUrl, client.client_uuid, 25)
      : this.portalService.activity(client.client_uuid, 25);

    request.subscribe({
      next: response => {
        this.activityData = response && response.data ? response.data : null;
        this.loading = false;
      },
      error: error => this.handleError(error, 'Client API activity could not be loaded.')
    });
  }

  rotateSecret(client: ClientApiAdminClient): void {
    const action = client.active_secret_count > 0 ? 'rotate' : 'generate';
    const impact = client.active_secret_count > 0
      ? 'Every existing secret and unexpired bearer token for this client will stop working immediately. Every connected application must be updated with the replacement secret.'
      : 'The new secret will be displayed once only and must be saved before leaving this page.';
    const confirmed = window.confirm(
      'Confirm ' + action + ' secret for ' + client.name + '? ' + impact
    );
    if (!confirmed) {
      return;
    }

    this.beginRequest();
    this.generatedSecret = '';
    this.generatedClientUuid = '';
    this.generatedClientName = '';
    this.generatedEnvironment = '';
    this.generatedAt = '';
    const request = this.mode === 'admin'
      ? this.adminService.rotateSecret(this.adminService.selectedBaseUrl, client.client_uuid, true, true)
      : this.portalService.rotateSecret(client.client_uuid);

    request.subscribe({
      next: response => {
        this.generatedSecret = response && response.data ? response.data.client_secret : '';
        this.generatedClientUuid = client.client_uuid;
        this.generatedClientName = client.name;
        this.generatedEnvironment = client.environment_mode;
        this.generatedAt = new Date().toISOString();
        const successMessage = client.active_secret_count > 0
          ? 'The replacement secret is shown once. Every previous secret and bearer token has been revoked.'
          : 'The new secret is shown once. Store it securely before leaving this page.';
        this.loading = false;
        this.loadAccess();
        this.message = successMessage;
      },
      error: error => this.handleError(error, 'Client API secret could not be changed.')
    });
  }

  copyValue(value: string, label: string): void {
    if (!value) {
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(() => {
        this.message = label + ' copied.';
      }).catch(() => this.copyValueFallback(value, label));
      return;
    }

    this.copyValueFallback(value, label);
  }

  copyCredentials(): void {
    this.copyValue(this.credentialsText(), 'Client API setup');
  }

  downloadCredentials(): void {
    if (!this.generatedSecret || !this.generatedClientUuid) {
      return;
    }

    const blob = new Blob([this.credentialsText()], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sharmans-client-api-' + this.safeFilePart(this.generatedClientName) + '-' + this.safeFilePart(this.generatedEnvironment) + '-credentials.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    this.message = 'One-time credential file downloaded. Move it to an approved secret manager, then delete the downloaded plaintext file.';
  }

  private copyValueFallback(value: string, label: string): void {
    const field = document.createElement('textarea');
    field.value = value;
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    document.body.removeChild(field);
    this.message = label + ' copied.';
  }

  private credentialsText(): string {
    return [
      'Sharmans Client API one-time credentials',
      '',
      'API client: ' + this.generatedClientName,
      'Mode: ' + this.generatedEnvironment,
      'Created: ' + this.generatedAt,
      '',
      'CLIENT_API_BASE_URL=' + this.clientApiBaseUrl,
      'CLIENT_API_CLIENT_ID=' + this.generatedClientUuid,
      'CLIENT_API_CLIENT_SECRET=' + this.generatedSecret,
      'CLIENT_API_PORTAL_URL=' + this.clientApiPortalUrl,
      'CLIENT_API_SAMPLE_APP_URL=' + this.clientApiSampleAppUrl,
      '',
      'IMPORTANT',
      '- This secret is displayed once and cannot be retrieved later.',
      '- Store it in the integrating application server-side secret manager or protected environment configuration.',
      '- Never place it in Angular/browser code, a URL, source control, email or a support ticket.',
      '- Open Guide and Swagger from API Access & Controls so the broker can issue the correct authenticated links.',
      '- If this secret was rotated, every previous secret and unexpired bearer token stopped working immediately.',
      '- After secure storage, delete this plaintext file from Downloads and any temporary location.'
    ].join('\r\n');
  }

  private safeFilePart(value: string): string {
    const safe = String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return safe || 'client';
  }

  scopeLabel(scope: string): string {
    return scope.replace('client-api:', '').replace(/:/g, ' ');
  }

  traceText(trace: any): string {
    if (!trace) {
      return '';
    }
    return Object.keys(trace)
      .filter(key => trace[key] !== null && trace[key] !== '' && typeof trace[key] !== 'object')
      .map(key => key + ': ' + trace[key])
      .join(' | ');
  }

  private beginRequest(): void {
    this.loading = true;
    this.message = '';
    this.error = '';
  }

  private handleError(error: any, fallback: string): void {
    const responseError = error && error.error && error.error.errors && error.error.errors.length
      ? error.error.errors[0].message
      : '';
    this.error = responseError || fallback;
    this.message = '';
    this.loading = false;
  }
}
